import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';

const Navbar: React.FC = () => {
  const [searchVisible, setSearchVisible] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { toggleLanguage } = useLanguage();
  const { user, logout } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/furniture?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchVisible(false);
      setSearchQuery('');
    }
  };

  return (
    <nav className="bg-indigo-600">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Link to="/" className="text-white font-bold text-xl">MassUniHub</Link>
            </div>
            <div className="hidden lg:block">
              <div className="ml-10 flex items-baseline space-x-4">
                <Link to="/" className="text-white px-3 py-2 rounded-md text-sm font-medium">{t('navbar.home')}</Link>
                <Link to="/furniture" className="text-white px-3 py-2 rounded-md text-sm font-medium">{t('navbar.browse')}</Link>
                <Link to="/sell" className="text-white px-3 py-2 rounded-md text-sm font-medium">{t('navbar.sell')}</Link>
              </div>
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            {user?.role === 'admin' && <Link to="/admin" className="rounded-md bg-indigo-700 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-800">{t('navbar.admin')}</Link>}
            {/* Search icon and input field */}
            <div className="relative">
              {searchVisible ? (
                <form onSubmit={handleSearchSubmit} className="flex items-center">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    placeholder={t('furniture.listing.searchPlaceholder')}
                    className="w-40 sm:w-64 py-1 px-3 rounded-l-md text-gray-900 text-sm focus:outline-none"
                    autoFocus
                  />
                  <button
                    type="submit"
                    aria-label={t('navbar.search')}
                    className="bg-indigo-700 text-white px-2 py-1 rounded-r-md hover:bg-indigo-800"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={() => setSearchVisible(false)}
                    aria-label={t('common.close')}
                    className="ml-1 text-gray-500 hover:text-gray-700"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </form>
              ) : (
                <button
                  onClick={() => setSearchVisible(true)}
                  aria-label={t('navbar.search')}
                  className="text-white hover:text-gray-200"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </button>
              )}
            </div>
            
            {/* Language toggle */}
            <button
              onClick={toggleLanguage}
              className="text-gray-200 hover:text-white px-2 py-1 rounded-md text-xs font-medium border border-gray-400"
              title={t('language.switch')}
            >
              {i18n.language === 'zh' ? 'EN' : '中文'}
            </button>
            
            <div className="hidden lg:block">
              <div className="flex items-center md:ml-6">
                {user ? (
                  <>
                    <span className="text-white px-3 py-2 text-sm">{t('navbar.welcome', { name: user.firstName })}</span>
                    <button onClick={handleLogout} className="bg-white text-indigo-600 px-3 py-2 rounded-md text-sm font-medium">{t('navbar.logout')}</button>
                  </>
                ) : (
                  <>
                    <Link to="/login" className="text-gray-200 hover:text-white px-3 py-2 rounded-md text-sm font-medium">{t('navbar.login')}</Link>
                    <Link to="/register" className="bg-white text-indigo-600 px-3 py-2 rounded-md text-sm font-medium">{t('navbar.signup')}</Link>
                  </>
                )}
              </div>
            </div>
          </div>
          
          <div className="lg:hidden flex items-center">
            <button
              type="button"
              className="text-white"
              aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={isMenuOpen}
              aria-controls="public-mobile-navigation"
              onClick={() => setIsMenuOpen((open) => !open)}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {isMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>
        {isMenuOpen && (
          <div id="public-mobile-navigation" role="group" aria-label="Mobile navigation" className="lg:hidden border-t border-indigo-500 px-2 pt-2 pb-3 space-y-1">
            <Link to="/" className="block px-3 py-2 text-white" onClick={() => setIsMenuOpen(false)}>{t('navbar.home')}</Link>
            <Link to="/furniture" className="block px-3 py-2 text-white" onClick={() => setIsMenuOpen(false)}>{t('navbar.browse')}</Link>
            <Link to="/sell" className="block px-3 py-2 text-white" onClick={() => setIsMenuOpen(false)}>{t('navbar.sell')}</Link>
            {user ? (
              <>
                <span className="block px-3 py-2 text-white">{t('navbar.welcome', { name: user.firstName })}</span>
                <button className="block px-3 py-2 text-white" onClick={() => { handleLogout(); setIsMenuOpen(false); }}>{t('navbar.logout')}</button>
              </>
            ) : (
              <>
                <Link to="/login" className="block px-3 py-2 text-white" onClick={() => setIsMenuOpen(false)}>{t('navbar.login')}</Link>
                <Link to="/register" className="block px-3 py-2 text-white" onClick={() => setIsMenuOpen(false)}>{t('navbar.signup')}</Link>
              </>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
